/**
 * Travel IA v2 全域導覽（FE-IA-V2-01）。
 * 主要入口 ownership 共用同一 model；Desktop／Tablet／Mobile 僅呈現不同。
 * Presentation-only — 既有 route／feature 保留相容，不改後端 ownership。
 *
 * Canonical order：探索 → 行程 → 我的行程 → ＋ → 通知 → 我的（手機＋獨立）
 */

export const TRAVEL_PRIMARY_DESTINATIONS = Object.freeze([
  {
    id: 'explore',
    kind: 'link',
    to: '/explore',
    labelKey: 'travel.nav.explore',
    end: true,
    auth: false,
    testId: 'nav-explore',
  },
  {
    id: 'public-journeys',
    kind: 'link',
    to: '/discover/journeys',
    labelKey: 'travel.journeyDiscovery.title',
    end: false,
    auth: false,
    testId: 'nav-public-journeys',
  },
  {
    id: 'journeys',
    kind: 'link',
    to: '/journeys',
    labelKey: 'travel.nav.myJourneys',
    end: false,
    auth: true,
    testId: 'nav-journeys',
  },
  {
    id: 'create',
    kind: 'create',
    labelText: '＋',
    auth: true,
    testId: 'nav-create',
  },
  {
    id: 'notifications',
    kind: 'link',
    to: '/notifications',
    labelKey: 'travel.nav.notifications',
    end: false,
    auth: true,
    testId: 'nav-notifications-primary',
  },
  {
    id: 'me',
    kind: 'link',
    to: '/me',
    labelKey: 'travel.nav.me',
    end: false,
    auth: true,
    testId: 'nav-me',
  },
]);

/** 主要入口 一律可見；受保護 route 由 ProtectedRoute 導向登入。 */
export function primaryDestinationsForAuth(_authenticated = true) {
  return TRAVEL_PRIMARY_DESTINATIONS;
}

/**
 * Desktop／Tablet／Mobile 共用同一 destination 清單（IA v2）。
 * 舊 webSidebarDestinations 擴充項（following／saved／groupTrips／messages）已移出 primary。
 */
export function webSidebarDestinations() {
  return TRAVEL_PRIMARY_DESTINATIONS;
}

export function isPrimaryDestinationActive(pathname, destination) {
  const path = String(pathname || '');
  if (!destination || destination.kind === 'create') {
    return false;
  }
  if (destination.id === 'public-journeys') {
    return path === '/discover/journeys' || path.startsWith('/public/journeys/');
  }
  if (destination.end) {
    return isExplorePath(path);
  }
  if (destination.id === 'explore') {
    return isExplorePath(path);
  }
  if (destination.id === 'journeys') {
    return path === '/journeys' || path.startsWith('/journeys/');
  }
  if (destination.id === 'notifications') {
    return isNotificationsPath(path);
  }
  if (destination.id === 'me') {
    return isMePath(path);
  }
  return path === destination.to || path.startsWith(`${destination.to}/`);
}

/** Desktop sidebar active helper — 與 primary 同一語意（單一 source of truth）。 */
export function isWebSidebarDestinationActive(pathname, destination) {
  return isPrimaryDestinationActive(pathname, destination);
}

function isExplorePath(path) {
  if (path === '/' || path === '/explore' || path.startsWith('/explore/')) return true;
  if (path === '/social/following' || path.startsWith('/social/following/')) return true;
  if (path === '/public/group-trips' || path.startsWith('/public/group-trips/')) return true;
  return false;
}

function isNotificationsPath(path) {
  if (path === '/notifications' || path.startsWith('/notifications/')) return true;
  if (path === '/messages' || path.startsWith('/messages/')) return true;
  if (path === '/group-trip-notices' || path.startsWith('/group-trip-notices/')) return true;
  return false;
}

function isMePath(path) {
  return path === '/me'
    || path.startsWith('/me/')
    || path === '/saved'
    || path.startsWith('/saved')
    || path === '/group-trips'
    || path.startsWith('/group-trips')
    || path === '/invitations'
    || path.startsWith('/invitations')
    || path === '/host/earnings'
    || path.startsWith('/host/');
  // /journeys 歸「我的行程」，不歸「我的」
}

/**
 * Route compatibility 盤點（FE-IA-V2-01；不改 URL）。
 * OLD_ROUTE → IA_V2_OWNER → CANONICAL_DESTINATION → COMPATIBILITY_ACTION
 */
export const IA_V2_ROUTE_COMPATIBILITY = Object.freeze([
  { oldRoute: '/explore?tab=journeys', owner: 'public-journeys', destination: '/discover/journeys', action: 'REDIRECT_WITH_FILTERS' },
  { oldRoute: '/explore', owner: 'explore', destination: '/explore', action: 'KEEP' },
  { oldRoute: '/social/following', owner: 'explore', destination: '/social/following', action: 'KEEP_UNDER_EXPLORE' },
  { oldRoute: '/explore?view=map', owner: 'explore', destination: '/explore?view=map', action: 'KEEP_MAP_REACHABLE' },
  { oldRoute: '/public/group-trips', owner: 'explore', destination: '/public/group-trips', action: 'KEEP_NOT_PRIMARY' },
  { oldRoute: '/public/journeys/:id', owner: 'public-journeys', destination: '/public/journeys/:id', action: 'KEEP' },
  { oldRoute: '/journeys', owner: 'journeys', destination: '/journeys', action: 'KEEP' },
  { oldRoute: '/journeys/:id/*', owner: 'journeys', destination: '/journeys/:id/*', action: 'KEEP_WORKSPACE' },
  { oldRoute: '/journeys/:id/planning', owner: 'journeys', destination: '/journeys/:id/planning', action: 'KEEP' },
  { oldRoute: '/journeys/:id/stays', owner: 'journeys', destination: '/journeys/:id/stays', action: 'KEEP' },
  { oldRoute: '/journeys/:id/transport', owner: 'journeys', destination: '/journeys/:id/transport', action: 'KEEP' },
  { oldRoute: '/journeys/:id/budget', owner: 'journeys.cost', destination: '/journeys/:id/budget', action: 'KEEP_COST' },
  { oldRoute: '/journeys/:id/people', owner: 'journeys', destination: '/journeys/:id/people', action: 'KEEP' },
  { oldRoute: '/group-trips/picker', owner: 'create', destination: '/group-trips/picker', action: 'KEEP_VIA_GLOBAL_CREATE' },
  { oldRoute: '/notifications', owner: 'notifications', destination: '/notifications', action: 'KEEP_AS_PRIMARY' },
  { oldRoute: '/messages', owner: 'notifications', destination: '/messages', action: 'KEEP_UNDER_INBOX' },
  { oldRoute: '/group-trip-notices', owner: 'notifications', destination: '/group-trip-notices', action: 'KEEP_UNDER_INBOX' },
  { oldRoute: '/me', owner: 'me', destination: '/me', action: 'KEEP' },
  { oldRoute: '/saved', owner: 'me', destination: '/saved', action: 'KEEP_NOT_PRIMARY' },
  { oldRoute: '/saved-journeys', owner: 'me', destination: '/saved?contentType=JOURNEY_PUBLICATION', action: 'KEEP_REDIRECT' },
  { oldRoute: '/me/personal-map', owner: 'me', destination: '/me/personal-map', action: 'KEEP' },
  { oldRoute: '/social/creators/:memberId', owner: 'profile', destination: '/social/creators/:memberId', action: 'KEEP_PUBLIC_AND_OWN' },
  { oldRoute: '/group-trips', owner: 'me', destination: '/group-trips', action: 'KEEP_MANAGE_CONTEXT' },
  { oldRoute: '/group-trips/new', owner: 'create', destination: '/group-trips/picker→new', action: 'KEEP_VIA_GLOBAL_CREATE' },
  { oldRoute: '/social/posts/new', owner: 'create', destination: '/social/posts/new', action: 'KEEP_VIA_GLOBAL_CREATE' },
  { oldRoute: '/social/check-ins/new', owner: 'create', destination: '/social/check-ins/new', action: 'KEEP_VIA_GLOBAL_CREATE' },
  { oldRoute: '/journeys/new', owner: 'create', destination: '/journeys/new', action: 'KEEP_VIA_GLOBAL_CREATE' },
]);
